import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchJson, postJson } from '../api';
import PollTiles from '../components/PollTiles';
import { useAuth } from '../lib/auth';
import { trackBoothView, trackBoothVote } from '../lib/analytics';
import { firstName, formatCount, percentOf } from '../lib/format';

const BATCH = 10;
const REFILL_AT = 2; // fetch more when this many are left

/**
 * The Voting Booth (spec §1): one undecided poll at a time, every option on
 * screen, a vote advances. Booth votes post `source: "booth"`; the server
 * decides whether one was "considered" and credits it.
 */
export default function BoothPage() {
  const auth = useAuth();
  const user = auth?.user || null;
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [waiting, setWaiting] = useState(null);
  const [credits, setCredits] = useState(null);
  const [earned, setEarned] = useState(0);
  const [banner, setBanner] = useState('');
  const [voting, setVoting] = useState(false);
  const [last, setLast] = useState(null);
  const seen = useRef(new Set());
  const bannerShown = useRef(false);
  const fetching = useRef(false);
  const exhausted = useRef(false);

  const fetchMore = useCallback(async (initial = false) => {
    if (fetching.current) return;
    fetching.current = true;
    try {
      const data = await fetchJson(`/booth/next?limit=${BATCH}`);
      const polls = Array.isArray(data?.polls) ? data.polls : [];
      exhausted.current = polls.length < BATCH;
      if (Number.isFinite(data?.waiting)) setWaiting(data.waiting);
      if (data && data.credits !== undefined) setCredits(data.credits);
      setQueue((current) => {
        const have = new Set(current.map((p) => p.id));
        const fresh = polls.filter((p) => p && !have.has(p.id) && !seen.current.has(p.id));
        return [...current, ...fresh];
      });
      if (initial) trackBoothView({ waiting: data?.waiting ?? null });
    } catch (_) {
      if (initial) setError('The booth is closed for a moment. Try again shortly.');
    } finally {
      fetching.current = false;
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMore(true); }, [fetchMore]);

  // Keep a few polls in hand: refill when the queue runs low.
  useEffect(() => {
    if (!loading && queue.length <= REFILL_AT && !exhausted.current) fetchMore(false);
  }, [loading, queue.length, fetchMore]);

  const poll = queue[0] || null;

  function advance(pollId) {
    seen.current.add(pollId);
    setQueue((current) => current.filter((p) => p.id !== pollId));
    setWaiting((w) => (Number.isFinite(w) ? Math.max(0, w - 1) : w));
  }

  async function vote(item) {
    if (!poll || voting || !item?.id) return;
    setVoting(true);
    setError('');
    try {
      const data = await postJson(`/polls/${poll.id}/vote`, { item_id: item.id, source: 'booth' });
      trackBoothVote(!!data.credited, { poll_id: poll.id });
      if (data.credited) setEarned((n) => n + 1);
      if (data.credits !== undefined && data.credits !== null) setCredits(data.credits);
      if (data.credit_message && !bannerShown.current) {
        bannerShown.current = true;
        setBanner(data.credit_message);
      }
      const tallies = Array.isArray(data.items) ? data.items : [];
      const picked = tallies.find((it) => it.id === item.id);
      setLast({
        caption: poll.caption,
        label: item.description || 'that one',
        pct: picked ? percentOf(picked.votes_count, data.votes_count) : null,
        credited: !!data.credited,
      });
      advance(poll.id);
    } catch (err) {
      if (err.status === 409 || err.status === 403 || err.status === 404) {
        advance(poll.id); // already voted, own poll, or gone — move on
      } else {
        setError('Couldn’t record that vote. Try again.');
      }
    } finally {
      setVoting(false);
    }
  }

  const asker = poll ? firstName(poll.author_name || poll.author_handle) : '';

  return (
    <main className="mx-auto flex w-full max-w-xl flex-col pb-6">
      <div className="flex items-center gap-3 bg-voto-chrome px-4 pb-3 pt-1">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-semibold text-white">Voting Booth</h1>
          <p className="text-xs text-voto-muted">
            {Number.isFinite(waiting) ? `${formatCount(waiting)} waiting` : 'Undecided polls'} · one credit per considered vote
          </p>
        </div>
        {credits !== null ? (
          <Link
            to="/credits"
            data-testid="booth-credits"
            className="flex-none rounded-lg bg-voto-surface px-2.5 py-1.5 text-xs font-semibold text-voto-fg ring-1 ring-voto-line transition hover:bg-voto-raised"
          >
            <span className="text-voto-coral">+{earned}</span> this session · {formatCount(credits)} total
          </Link>
        ) : (
          !user && (
            <Link to="/auth" className="flex-none rounded-lg px-2.5 py-1.5 text-xs font-semibold text-voto-coral ring-1 ring-voto-line hover:bg-voto-raised">
              Sign in to earn credits
            </Link>
          )
        )}
      </div>

      {banner && (
        <div role="status" className="mx-4 mt-3 flex items-start gap-3 rounded-xl bg-voto-surface px-3.5 py-2.5 text-sm text-voto-fg ring-1 ring-voto-line">
          <p className="min-w-0 flex-1">
            {banner}
            {!user && (
              <>
                {' '}
                <Link to="/auth" className="font-semibold text-voto-coral hover:underline">Sign in</Link>
              </>
            )}
          </p>
          <button type="button" aria-label="Dismiss" onClick={() => setBanner('')} className="rounded px-1 leading-none text-voto-muted hover:text-voto-fg">
            ×
          </button>
        </div>
      )}

      {loading && <p className="px-4 py-10 text-voto-muted">Opening the booth…</p>}
      {!loading && error && !poll && <p className="mx-4 mt-6 rounded-lg bg-voto-coral/10 p-3 text-voto-coral">{error}</p>}

      {!loading && !poll && !error && (
        <div className="px-6 py-16 text-center">
          <p className="text-lg font-semibold text-voto-fg">
            The booth is empty. <Link to="/polls/new" className="text-voto-coral hover:underline">Ask something.</Link>
          </p>
          <p className="mt-2 text-sm text-voto-muted">New polls land here first. Yours will too.</p>
        </div>
      )}

      {poll && (
        <section aria-label="Poll in the booth" data-testid="booth-poll" className="px-4 pt-4">
          <p className="text-xs font-semibold text-voto-coral">
            {poll.boost ? 'Boosted · ' : ''}
            {asker ? `${asker} is asking` : 'From the archive'}
          </p>
          <h2 className="mt-1 text-xl font-semibold leading-snug text-voto-fg">{poll.caption}</h2>
          <div className="mt-3">
            <PollTiles
              key={poll.id}
              src={poll.image_url}
              items={poll.items}
              count={poll.items_count || poll.items?.length}
              alt={poll.caption || 'Poll image'}
              variant="rounded"
              layout="fill"
              onVote={vote}
              voting={voting}
            />
          </div>
          <div className="mt-3 flex items-center gap-3 text-sm">
            <span className="text-voto-muted">Tap a photo to vote.</span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => advance(poll.id)}
              disabled={voting}
              className="rounded-lg px-3 py-1.5 font-medium text-voto-muted ring-1 ring-voto-line transition hover:text-voto-fg disabled:opacity-50"
            >
              Skip
            </button>
          </div>
          {error && <p className="mt-2 text-sm text-voto-coral">{error}</p>}
        </section>
      )}

      {last && (
        <p data-testid="booth-last" className="mx-4 mt-4 rounded-xl bg-voto-surface px-3.5 py-2.5 text-xs text-voto-muted">
          Last vote: <span className="text-voto-fg">{last.label}</span>
          {last.pct !== null ? ` · ${last.pct}% agree` : ''}
          {last.credited ? <span className="text-voto-coral"> · +1 credit</span> : ''}
        </p>
      )}
    </main>
  );
}
